# Portal del Cliente

![Portal del Cliente](https://assets.elevenestudio.me/changelog-idc/CLIENT_PORTAL_IDC_RE_DEMO.png)

Esta actualización incorpora el **Portal del Cliente**, una nueva forma de recopilar, revisar y revalidar la información de los clientes sin depender de formularios manuales, correos sueltos o seguimiento fuera de la plataforma.

Ahora puedes enviarle al cliente un enlace seguro para que complete su expediente, adjunte documentos, corrija observaciones y confirme sus datos. El equipo interno puede revisar todo desde la plataforma, pedir cambios puntuales y aprobar el expediente para actualizar el registro del cliente.

---

## Portal público para clientes

El cliente recibe un enlace por correo y puede completar su información sin necesitar una cuenta en la plataforma. El formulario está organizado por pasos, guarda el avance y muestra con claridad qué información falta antes de enviarlo.

### ¿Qué puede hacer el cliente?
- Completar sus datos personales o los datos de una empresa
- Confirmar su identidad con RENAP cuando aplica
- Completar información de contacto, PEP, económica y patrimonial
- Registrar representantes legales cuando el expediente lo requiere
- Adjuntar los documentos solicitados
- Guardar su avance y continuar más tarde desde el mismo enlace
- Enviar comentarios cuando el expediente ya está en revisión
- Corregir únicamente los campos o documentos observados por el equipo interno

> El portal también soporta expedientes de persona individual y persona jurídica, incluyendo empresa, representante legal, información económica, PEP y estado patrimonial.

---

## Revisión interna del expediente

El equipo ahora cuenta con una vista dedicada para revisar el expediente completo del cliente. La revisión se puede hacer por sección y por documento, dejando observaciones claras para que el cliente sepa exactamente qué debe corregir.

### ¿Qué puedes hacer desde la plataforma?
- Crear y enviar solicitudes de verificación desde el cliente
- Revisar el expediente por secciones: datos generales, empresa, representante legal, PEP, económica, patrimonial y documentos
- Agregar anotaciones y comentarios visibles para el seguimiento interno
- Pedir correcciones al cliente sin reiniciar todo el proceso
- Aprobar el expediente y actualizar el registro interno del cliente
- Rechazar o cancelar un flujo cuando ya no corresponde continuar
- Ver el historial de acciones del expediente: aperturas, envíos, comentarios, revisiones, correcciones y aprobaciones

---

## Revalidación anual de clientes

También se agregó el flujo de **revalidación anual**, pensado para mantener actualizada la información de los clientes durante el ciclo de vida de sus negocios.

La plataforma identifica a los clientes que deben revalidar, permite enviarles un enlace con sus datos ya prellenados y muestra al equipo qué cambió respecto a la información anterior. Si el cliente confirma sin cambios, también queda registrado.

### ¿Qué incluye?
- Enlaces de revalidación con la información actual del cliente ya cargada
- Comparación entre los datos anteriores y los nuevos durante la revisión
- Estados de revalidación en la lista de clientes: al día, vencido, en curso, contrato firmado o sin verificación
- Indicadores para ver cuántos clientes están al día, por revalidar o fuera del ciclo
- Recordatorios para clientes que todavía no completaron su formulario
- Renovación de enlaces vencidos para que el cliente pueda retomar el proceso
- Avisos internos para que el asesor y el equipo sepan cuándo se inició una revalidación

---

## Mejoras en Clientes

La lista y el detalle de clientes también recibieron mejoras para acompañar el nuevo flujo.

- Nueva vista de clientes con filtros por tipo de persona, asesor, representante legal, estado del flujo y tipo de validación
- Panel lateral renovado para consultar la información del cliente y sus flujos
- Historial de flujos anteriores dentro del detalle del cliente
- Indicadores de revalidación directamente en la lista
- Alta rápida de clientes desde el nuevo asistente, con validación de identidad cuando corresponde
- Mejor explicación cuando un cliente ya existe, con acceso directo al registro existente

---

## Permisos y roles

El Portal del Cliente ahora tiene permisos propios para que cada rol tenga acceso solo a las acciones que le corresponden.

- Crear y enviar solicitudes
- Consultar solicitudes y métricas
- Revisar expedientes
- Aprobar y sincronizar información
- Rechazar expedientes
- Cancelar flujos
- Renovar enlaces vencidos
- Administrar correcciones de otros revisores
- Configurar definiciones de flujo

Además, la pantalla de roles ahora organiza los permisos en una matriz más clara, agrupada por módulo, para que sea más fácil configurar accesos sin perder visibilidad.

---

## Correcciones y ajustes

- Mejoras visuales en la revisión del expediente y en la sección de documentos
- Correcciones para que los documentos requeridos aparezcan de forma clara según el tipo de cliente
- Mejor manejo de campos verificados por RENAP
- Mejoras en mensajes de error cuando faltan datos o existe información duplicada
- Ajustes en el historial para mostrar nombres de campos más claros
- Mejoras de carga visual en Clientes e Inventario
- Ajustes de permisos para que las acciones del portal respeten correctamente el rol del usuario
